import React, { useState, useRef, useEffect } from 'react';
import { Search, X, MousePointerClick, Navigation, Scroll, FileText, Download, Upload, Eye, Zap } from 'lucide-react';
import { createPortal } from 'react-dom';

/**
 * Modular Filter Popup Component
 * 
 * A comprehensive, reusable filter popup that opens positioned relative to its trigger.
 * Features:
 * - Search bar at the top
 * - Left navigation for filter types
 * - Right panel for filter properties based on selected type
 * - Configurable for different pages/use cases
 * 
 * @param {Object} props
 * @param {Boolean} props.isOpen - Whether the popup is open
 * @param {Function} props.onClose - Callback when popup should close
 * @param {React.RefObject} props.triggerRef - Ref to the element that triggered the popup (for positioning)
 * @param {Array} props.navItems - Array of navigation items for the left panel
 * @param {Object} props.filterConfigs - Object mapping nav item IDs to filter configurations
 * @param {Array} props.filters - Current filters array
 * @param {Function} props.onFiltersChange - Callback when filters change
 * @param {Function} props.getFieldOptions - Function to get options for a field
 * @param {Function} props.onEventSelect - Callback when an event is selected (for Events page)
 * @param {Array} props.availableEvents - Array of available events to display (for Events page)
 * @param {Boolean} props.loadingEvents - Whether events are loading (for Events page)
 */
const FilterPopup = ({
  isOpen,
  onClose,
  triggerRef,
  navItems = [],
  filterConfigs = {},
  filters = [],
  onFiltersChange,
  getFieldOptions = () => [],
  onEventSelect,
  availableEvents = [],
  loadingEvents = false
}) => {
  const [selectedNavItem, setSelectedNavItem] = useState(navItems[0]?.id || null);
  const [searchTerm, setSearchTerm] = useState('');
  const [eventSearchTerm, setEventSearchTerm] = useState(''); // Separate search for events
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const popupRef = useRef(null);

  // Calculate position relative to trigger element
  useEffect(() => {
    if (isOpen && triggerRef?.current) {
      const triggerRect = triggerRef.current.getBoundingClientRect();
      const scrollY = window.scrollY || window.pageYOffset;
      const scrollX = window.scrollX || window.pageXOffset;

      setPosition({
        top: triggerRect.bottom + scrollY + 8, // 8px gap below trigger
        left: triggerRect.left + scrollX
      });
    }
  }, [isOpen, triggerRef]);

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        isOpen &&
        popupRef.current &&
        !popupRef.current.contains(event.target) &&
        triggerRef?.current &&
        !triggerRef.current.contains(event.target)
      ) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen, onClose, triggerRef]);

  // Close on Escape key
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const currentConfig = filterConfigs[selectedNavItem] || {};
  // Only filter nav items if not showing events list
  const filteredNavItems = (onEventSelect && selectedNavItem === 'all') 
    ? navItems 
    : navItems.filter(item =>
        item.label.toLowerCase().includes(searchTerm.toLowerCase())
      );

  return createPortal(
    <div
      ref={popupRef}
      className="fixed z-50 bg-white border border-gray-200 rounded-lg shadow-xl flex flex-col overflow-hidden"
      style={{
        top: `${position.top}px`,
        left: `${position.left}px`,
        width: '520px',
        height: '320px',
        fontFamily: 'Raleway, sans-serif'
      }}
    >
      {/* Search Bar */}
      <div className="px-4 py-3 border-b border-gray-200 flex-shrink-0">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-[#475569]" />
          <input
            type="text"
            placeholder={onEventSelect && selectedNavItem === 'all' ? "Search events..." : "Search filter types..."}
            value={onEventSelect && selectedNavItem === 'all' ? eventSearchTerm : searchTerm}
            onChange={(e) => {
              if (onEventSelect && selectedNavItem === 'all') {
                setEventSearchTerm(e.target.value);
              } else {
                setSearchTerm(e.target.value);
              }
            }}
            className="w-full pl-10 pr-10 py-2 text-sm rounded-lg bg-gray-100 focus:outline-none focus:ring-2 focus:ring-[#1d0c46] focus:bg-white border-0"
          />
          {(searchTerm || eventSearchTerm) && (
            <button
              type="button"
              onClick={() => {
                setSearchTerm('');
                setEventSearchTerm('');
              }}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-[#475569] hover:text-[#475569]"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>

      {/* Main Content: Left Nav + Right Filters */}
      <div className="flex flex-1 min-h-0 overflow-hidden">
        {/* Left Navigation */}
        <div className="w-32 border-r border-gray-200 bg-gray-50 flex-shrink-0 overflow-y-auto min-h-0">
          <div className="py-2">
            {filteredNavItems.length === 0 ? (
              <div className="px-4 py-2 text-sm text-[#475569]">No filter types found</div>
            ) : (
              filteredNavItems.map((item) => (
                <button
                  key={item.id}
                  onClick={() => setSelectedNavItem(item.id)}
                  className={`w-full text-left px-4 py-2 text-sm transition-colors ${
                    selectedNavItem === item.id
                      ? 'bg-white border-l-2 border-[#4b57aa] text-[#4b57aa] font-semibold'
                      : 'text-[#475569] hover:bg-gray-100'
                  }`}
                >
                  {item.label}
                </button>
              ))
            )}
          </div>
        </div>

        {/* Right Filter Panel */}
        <div className="flex-1 min-h-0 overflow-y-auto">
          {/* Special handling for Events page - show event list */}
          {onEventSelect && selectedNavItem === 'all' ? (
            <EventsListPanel
              events={availableEvents}
              loading={loadingEvents}
              onEventSelect={onEventSelect}
              searchTerm={eventSearchTerm}
            />
          ) : currentConfig.fields && currentConfig.fields.length > 0 ? (
            <FilterPanel
              filters={filters.filter(f => f.category === selectedNavItem || !f.category)}
              onFiltersChange={(updatedFilters) => {
                // Merge with filters from other categories
                const otherFilters = filters.filter(f => f.category && f.category !== selectedNavItem);
                onFiltersChange([...otherFilters, ...updatedFilters.map(f => ({ ...f, category: selectedNavItem }))]);
              }}
              config={currentConfig}
              getFieldOptions={getFieldOptions}
            />
          ) : (
            <div className="p-6 text-sm text-[#475569]">
              No filters available for this category.
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

/**
 * Filter Panel Component
 * Renders the actual filter items for the selected category
 */
const FilterPanel = ({ filters, onFiltersChange, config, getFieldOptions }) => {
  const handleFilterChange = (filterId, updates) => {
    const updatedFilters = filters.map(filter =>
      filter.id === filterId ? { ...filter, ...updates } : filter
    );
    onFiltersChange(updatedFilters);
  };

  const handleRemoveFilter = (filterId) => {
    const updatedFilters = filters.filter(filter => filter.id !== filterId);
    onFiltersChange(updatedFilters);
  };

  const handleAddFilter = () => {
    const newFilter = {
      id: `filter-${Date.now()}-${Math.random()}`,
      field: '',
      operator: '',
      value: null,
      value2: null
    };
    onFiltersChange([...filters, newFilter]);
  };

  return (
    <div className="p-4">
      {filters.length === 0 ? (
        <div className="text-sm text-[#475569] py-4">
          No filters applied. Click "+ Add" to add a filter.
        </div>
      ) : (
        <div className="space-y-3">
          {filters.map((filter) => (
            <FilterItem
              key={filter.id}
              filter={filter}
              config={config}
              onFilterChange={(updates) => handleFilterChange(filter.id, updates)}
              onRemove={() => handleRemoveFilter(filter.id)}
              getFieldOptions={getFieldOptions}
            />
          ))}
        </div>
      )}

      {/* Add Filter Button */}
      <div className="mt-4">
        <button
          onClick={handleAddFilter}
          className="flex items-center gap-2 text-sm text-[#475569] hover:text-[#0f172a] transition-colors"
        >
          <span className="text-lg">+</span>
          <span>Add</span>
        </button>
      </div>
    </div>
  );
};

/**
 * Individual Filter Item Component
 */
const FilterItem = ({ filter, config, onFilterChange, onRemove, getFieldOptions }) => {
  const { field, operator, value, value2 } = filter;
  const [fieldOptions, setFieldOptions] = useState([]);
  const [loadingOptions, setLoadingOptions] = useState(false);
  
  const fieldConfig = config.fields?.find(f => f.id === field);

  // Load field options when field changes
  useEffect(() => {
    const loadOptions = async () => {
      if (!field || !fieldConfig) {
        setFieldOptions([]);
        return;
      }

      // Use static options if available
      if (fieldConfig.options) {
        setFieldOptions(fieldConfig.options);
        return;
      }

      // Load dynamic options
      if (fieldConfig.type === 'multiselect') {
        setLoadingOptions(true);
        try {
          const options = await getFieldOptions(field);
          setFieldOptions(Array.isArray(options) ? options : []);
        } catch (error) {
          console.error('Error loading field options:', error);
          setFieldOptions([]);
        } finally {
          setLoadingOptions(false);
        }
      } else {
        setFieldOptions([]);
      }
    };

    loadOptions();
  }, [field, fieldConfig, getFieldOptions]);

  const handleFieldChange = (newField) => {
    const newFieldConfig = config.fields?.find(f => f.id === newField);
    onFilterChange({
      field: newField,
      operator: newFieldConfig?.defaultOperator || config.defaultOperators?.[newFieldConfig?.type] || 'equals',
      value: null,
      value2: null
    });
  };

  const handleOperatorChange = (newOperator) => {
    onFilterChange({ operator: newOperator });
  };

  const handleValueChange = (newValue) => {
    onFilterChange({ value: newValue });
  };

  const handleValue2Change = (newValue2) => {
    onFilterChange({ value2: newValue2 });
  };

  if (!fieldConfig && field) {
    return null;
  }

  return (
    <div className="flex items-start gap-3 p-3 bg-transparent rounded-lg border border-dotted border-gray-300">
      <div className="flex-1 grid grid-cols-12 gap-2">
        {/* Field Selector */}
        <div className="col-span-3">
          <label className="block text-xs font-medium text-[#475569] mb-1">Field</label>
          <select
            value={field || ''}
            onChange={(e) => handleFieldChange(e.target.value)}
            className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-[#1d0c46]"
          >
            <option value="">Select field...</option>
            {config.fields?.map(f => (
              <option key={f.id} value={f.id}>{f.label}</option>
            ))}
          </select>
        </div>

        {/* Operator Selector */}
        {fieldConfig && (
          <div className="col-span-2">
            <label className="block text-xs font-medium text-[#475569] mb-1">Operator</label>
            <select
              value={operator || ''}
              onChange={(e) => handleOperatorChange(e.target.value)}
              className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-[#1d0c46]"
            >
              {config.operators?.[fieldConfig.type]?.map(op => (
                <option key={op.value} value={op.value}>{op.label}</option>
              ))}
            </select>
          </div>
        )}

        {/* Value Input(s) */}
        {fieldConfig && (
          <div className={operator === 'between' ? 'col-span-3' : 'col-span-5'}>
            <label className="block text-xs font-medium text-[#475569] mb-1">Value</label>
            {fieldConfig.type === 'multiselect' ? (
              <MultiselectInput
                value={value || []}
                options={fieldOptions}
                onChange={handleValueChange}
                placeholder={loadingOptions ? "Loading..." : "Select values..."}
              />
            ) : fieldConfig.type === 'boolean' ? (
              <select
                value={value || ''}
                onChange={(e) => handleValueChange(e.target.value)}
                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-[#1d0c46]"
              >
                <option value="true">True</option>
                <option value="false">False</option>
              </select>
            ) : fieldConfig.type === 'date' || fieldConfig.type === 'datetime' ? (
              <input
                type={fieldConfig.type === 'date' ? 'date' : 'datetime-local'}
                value={value || ''}
                onChange={(e) => handleValueChange(e.target.value)}
                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-[#1d0c46]"
              />
            ) : fieldConfig.type === 'number' ? (
              <input
                type="number"
                value={value || ''}
                onChange={(e) => handleValueChange(e.target.value)}
                placeholder="Enter value..."
                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-[#1d0c46]"
              />
            ) : (
              <input
                type="text"
                value={value || ''}
                onChange={(e) => handleValueChange(e.target.value)}
                placeholder="Enter value..."
                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-[#1d0c46]"
              />
            )}
          </div>
        )}

        {/* Second Value Input (for 'between' operator) */}
        {fieldConfig && operator === 'between' && (
          <div className="col-span-3">
            <label className="block text-xs font-medium text-[#475569] mb-1">To</label>
            {fieldConfig.type === 'date' || fieldConfig.type === 'datetime' ? (
              <input
                type={fieldConfig.type === 'date' ? 'date' : 'datetime-local'}
                value={value2 || ''}
                onChange={(e) => handleValue2Change(e.target.value)}
                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-[#1d0c46]"
              />
            ) : (
              <input
                type={fieldConfig.type === 'number' ? 'number' : 'text'}
                value={value2 || ''}
                onChange={(e) => handleValue2Change(e.target.value)}
                placeholder="Enter value..."
                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-[#1d0c46]"
              />
            )}
          </div>
        )}

        {/* Remove Button */}
        <div className="col-span-1 flex items-end">
          <button
            onClick={onRemove}
            className="p-1.5 text-[#475569] hover:text-red-600 transition-colors"
            aria-label="Remove filter"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

/**
 * Multiselect Input Component
 */
const MultiselectInput = ({ value = [], options = [], onChange, placeholder = "Select values..." }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const filteredOptions = options.filter(opt =>
    opt.label?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    opt.value?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const toggleOption = (optionValue) => {
    const newValue = value.includes(optionValue)
      ? value.filter(v => v !== optionValue)
      : [...value, optionValue];
    onChange(newValue);
  };

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg bg-white text-left focus:outline-none focus:ring-2 focus:ring-[#1d0c46] flex items-center justify-between"
      >
        <span className={value.length === 0 ? 'text-[#475569]' : ''}>
          {value.length === 0 ? placeholder : `${value.length} selected`}
        </span>
        <span className="text-[#475569]">▼</span>
      </button>

      {isOpen && (
        <>
          <div
            className="fixed inset-0 z-10"
            onClick={() => setIsOpen(false)}
          />
          <div className="absolute z-20 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-auto">
            <div className="p-2 border-b border-gray-200">
              <input
                type="text"
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-[#1d0c46]"
                onClick={(e) => e.stopPropagation()}
              />
            </div>
            <div className="py-1">
              {filteredOptions.length === 0 ? (
                <div className="px-3 py-2 text-sm text-[#475569]">No options found</div>
              ) : (
                filteredOptions.map(option => (
                  <label
                    key={option.value}
                    className="flex items-center px-3 py-2 hover:bg-gray-100 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={value.includes(option.value)}
                      onChange={() => toggleOption(option.value)}
                      className="mr-2"
                    />
                    <span className="text-sm">{option.label || option.value}</span>
                  </label>
                ))
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

/**
 * Get icon for event based on event name and type
 */
const getEventIcon = (eventName, eventType) => {
  if (eventType === 'custom') {
    return MousePointerClick;
  }

  // Map common auto event names to icons
  const eventNameLower = eventName?.toLowerCase() || '';
  
  if (eventNameLower.includes('page_view') || eventNameLower.includes('pageview')) {
    return Navigation;
  }
  if (eventNameLower.includes('click')) {
    return MousePointerClick;
  }
  if (eventNameLower.includes('scroll')) {
    return Scroll;
  }
  if (eventNameLower.includes('form') || eventNameLower.includes('submit')) {
    return FileText;
  }
  if (eventNameLower.includes('download')) {
    return Download;
  }
  if (eventNameLower.includes('upload')) {
    return Upload;
  }
  if (eventNameLower.includes('view') || eventNameLower.includes('impression')) {
    return Eye;
  }
  
  // Default icon for other auto events
  return Zap;
};

/**
 * Events List Panel Component
 * Shows a list of available events for selection
 */
const EventsListPanel = ({ events, loading, onEventSelect, searchTerm = '' }) => {
  const filteredEvents = events.filter(event =>
    event.eventName?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="p-6 text-sm text-[#475569] text-center">
        Loading events...
      </div>
    );
  }

  if (filteredEvents.length === 0) {
    return (
      <div className="p-6 text-sm text-[#475569] text-center">
        {searchTerm ? 'No events found matching your search.' : 'No events available.'}
      </div>
    );
  }

  return (
    <div className="p-3">
      <div className="space-y-1">
        {filteredEvents.map((event, idx) => {
          const Icon = getEventIcon(event.eventName, event.eventType);
          return (
            <button
              key={event.id != null ? `${event.id}-${idx}` : `event-${idx}`}
              onClick={() => onEventSelect(event)}
              className="w-full text-left px-2 py-1.5 text-sm text-[#0f172a] hover:bg-gray-50 rounded transition-colors flex items-center gap-2"
            >
              <Icon className="h-4 w-4 text-[#475569] flex-shrink-0" />
              <span className="flex-1">
                {event.eventName}
                {event.eventType === 'auto' && <span className="text-[#0f172a]"> [AUTO]</span>}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default FilterPopup;
